/*
  Carrossel das sections `enner-*` da home (hero, categorias, lançamentos).

  <enner-carrossel data-autoplay="7000" data-loop>
    <div data-trilho> ...itens... </div>
    <button data-anterior> <button data-proximo>
    <span data-contador-atual>1</span>
  </enner-carrossel>
  <button data-ponto> (um por item, opcional; ficam dentro do carrossel ou do ancestral
                       marcado com data-carrossel-raiz)

  O trilho rola com encaixe (CSS em assets/enner-home.css), então o dedo desliza no celular
  sem JS. Aqui só ficam as setas, o estado desabilitado delas, o contador "1/4", as bolinhas
  (aria-current no slide atual), a troca automática opcional e o foco do bloco escolhido no
  editor de temas.

  data-loop (opt-in; só o hero usa): "próximo" no último item volta ao primeiro, "anterior" no
  primeiro vai ao último, e as setas nunca ficam desabilitadas. Sem ele, as setas desabilitam
  nas pontas, como no Figma de categorias e lançamentos.

  data-autoplay (ms): troca sozinho em ciclo. Pausa com mouse em cima, foco de teclado dentro,
  dedo ou arrasto, e depois de qualquer interação conta o intervalo inteiro de novo. Não roda com
  prefers-reduced-motion: reduce nem com a aba em segundo plano.
*/
(() => {
  if (customElements.get('enner-carrossel')) return;

  class EnnerCarrossel extends HTMLElement {
    connectedCallback() {
      this.trilho = this.querySelector('[data-trilho]');
      if (!this.trilho) return;
      this.anterior = this.querySelector('[data-anterior]');
      this.proximo = this.querySelector('[data-proximo]');
      this.contador = this.querySelector('[data-contador-atual]');
      this.raiz = this.closest('[data-carrossel-raiz]') || this;
      this.pontos = Array.from(this.raiz.querySelectorAll('[data-ponto]'));
      this.loop = this.hasAttribute('data-loop');

      this.aoRolar = () => {
        cancelAnimationFrame(this.quadro);
        this.quadro = requestAnimationFrame(() => this.atualizar());
      };
      this.aoAnterior = () => this.ir(-1, this.loop);
      this.aoProximo = () => this.ir(1, this.loop);
      this.aoPonto = (evento) => {
        const itens = this.itens();
        const item = itens[this.pontos.indexOf(evento.currentTarget)];
        if (item) this.rolarPara(item);
      };
      this.aoEscolherBloco = (evento) => {
        if (!this.trilho.contains(evento.target)) return;
        const item = Array.from(this.trilho.children).find((el) => el.contains(evento.target));
        if (item) this.rolarPara(item);
        this.pausado = true;
      };

      this.trilho.addEventListener('scroll', this.aoRolar, { passive: true });
      window.addEventListener('resize', this.aoRolar);
      if (this.anterior) this.anterior.addEventListener('click', this.aoAnterior);
      if (this.proximo) this.proximo.addEventListener('click', this.aoProximo);
      this.pontos.forEach((ponto) => ponto.addEventListener('click', this.aoPonto));
      document.addEventListener('shopify:block:select', this.aoEscolherBloco);
      this.atualizar();

      const intervalo = parseInt(this.dataset.autoplay || '0', 10);
      const semMovimento = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (intervalo > 0 && !semMovimento && this.itens().length > 1) this.iniciarAutoplay(intervalo);
    }

    // Autoplay com temporizador que recomeça a cada interação: o slide só troca depois de um
    // intervalo inteiro sem mouse, foco, dedo ou clique nas setas e bolinhas.
    iniciarAutoplay(intervalo) {
      this.intervalo = intervalo;
      this.interagindo = false;
      this.agendar = () => {
        window.clearTimeout(this.relogio);
        this.relogio = window.setTimeout(() => {
          const parado = this.pausado || this.interagindo || this.matches(':hover') || this.raiz.querySelector(':focus-visible') || document.visibilityState !== 'visible';
          if (!parado) this.ir(1, true);
          this.agendar();
        }, this.intervalo);
      };
      this.aoEntrar = () => {
        this.interagindo = true;
      };
      this.aoSair = () => {
        this.interagindo = false;
        this.agendar();
      };
      this.aoTocar = () => {
        this.interagindo = true;
        window.clearTimeout(this.soltar);
      };
      // Depois do toque a rolagem por inércia ainda anda; só retoma o autoplay com folga.
      this.aoSoltar = () => {
        window.clearTimeout(this.soltar);
        this.soltar = window.setTimeout(this.aoSair, 800);
      };
      this.aoClicarControle = () => this.agendar();
      this.escutasAutoplay = [
        ['mouseenter', this.aoEntrar],
        ['mouseleave', this.aoSair],
        ['touchstart', this.aoTocar],
        ['touchend', this.aoSoltar],
        ['touchcancel', this.aoSoltar],
        ['pointerdown', this.aoEntrar],
        ['pointerup', (e) => (e.pointerType === 'mouse' ? this.aoClicarControle() : null)],
      ];
      this.escutasAutoplay.forEach(([nome, fn]) => this.addEventListener(nome, fn, { passive: true }));
      this.controles = [this.anterior, this.proximo, ...this.pontos].filter(Boolean);
      this.controles.forEach((controle) => controle.addEventListener('click', this.aoClicarControle));
      this.agendar();
    }

    disconnectedCallback() {
      if (!this.trilho) return;
      this.trilho.removeEventListener('scroll', this.aoRolar);
      window.removeEventListener('resize', this.aoRolar);
      if (this.anterior) this.anterior.removeEventListener('click', this.aoAnterior);
      if (this.proximo) this.proximo.removeEventListener('click', this.aoProximo);
      this.pontos.forEach((ponto) => ponto.removeEventListener('click', this.aoPonto));
      document.removeEventListener('shopify:block:select', this.aoEscolherBloco);
      window.clearTimeout(this.relogio);
      window.clearTimeout(this.soltar);
      (this.escutasAutoplay || []).forEach(([nome, fn]) => this.removeEventListener(nome, fn));
      (this.controles || []).forEach((controle) => controle.removeEventListener('click', this.aoClicarControle));
    }

    itens() {
      return Array.from(this.trilho.children).filter((el) => el.offsetParent !== null || el.getClientRects().length);
    }

    posicao(item) {
      const primeiro = this.itens()[0];
      return item.offsetLeft - (primeiro ? primeiro.offsetLeft : 0);
    }

    indiceAtual() {
      const x = this.trilho.scrollLeft;
      let melhor = 0;
      let distancia = Infinity;
      this.itens().forEach((item, i) => {
        const d = Math.abs(this.posicao(item) - x);
        if (d < distancia) {
          distancia = d;
          melhor = i;
        }
      });
      return melhor;
    }

    rolarPara(item) {
      const maximo = this.trilho.scrollWidth - this.trilho.clientWidth;
      this.trilho.scrollTo({ left: Math.min(this.posicao(item), maximo), behavior: 'smooth' });
    }

    // voltarAoInicio: nas pontas, "próximo" volta ao primeiro e "anterior" vai ao último.
    ir(direcao, voltarAoInicio) {
      const itens = this.itens();
      if (!itens.length) return;
      const maximo = this.trilho.scrollWidth - this.trilho.clientWidth;
      if (direcao > 0 && this.trilho.scrollLeft >= maximo - 2) {
        if (voltarAoInicio) this.trilho.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }
      if (direcao < 0 && this.trilho.scrollLeft <= 2) {
        if (voltarAoInicio) this.rolarPara(itens[itens.length - 1]);
        return;
      }
      const alvo = Math.max(0, Math.min(itens.length - 1, this.indiceAtual() + direcao));
      this.rolarPara(itens[alvo]);
    }

    atualizar() {
      const maximo = this.trilho.scrollWidth - this.trilho.clientWidth;
      const x = this.trilho.scrollLeft;
      const noFim = maximo > 2 && x >= maximo - 2;
      // Em loop as setas ficam sempre ativas (só somem, no HTML, quando há um item só).
      if (this.anterior) this.anterior.disabled = this.loop ? false : x <= 2;
      if (this.proximo) this.proximo.disabled = this.loop ? false : maximo <= 2 || noFim;
      const atual = noFim ? this.itens().length - 1 : this.indiceAtual();
      this.pontos.forEach((ponto, i) => {
        if (i === atual) ponto.setAttribute('aria-current', 'true');
        else ponto.removeAttribute('aria-current');
      });
      // Com vários itens visíveis de uma vez, o último nunca chega à esquerda: no fim da
      // rolagem o contador mostra o total (ex.: "4/4").
      if (this.contador) this.contador.textContent = String(noFim ? this.itens().length : this.indiceAtual() + 1);
    }
  }

  customElements.define('enner-carrossel', EnnerCarrossel);
})();

/*
  Parcelas do app (Scala) dentro do preço dos cards: o app injeta "3x de R$ 189,00 sem juros"
  como texto corrido dentro de <p>, sem nó próprio para "sem juros". Aqui esse trecho vira um
  <span class="enner-sem-juros"> para o CSS (assets/enner-home.css) quebrar a linha logo antes
  dele: "3x de R$ 189,00" / "sem juros". Um observador refaz o ajuste quando o app reescreve o
  preço (carrega depois da página, ou ao trocar de variante). Se o texto não tiver esse formato,
  nada muda e vale o CSS padrão.
*/
(() => {
  if (window.EnnerParcelas) return;
  window.EnnerParcelas = true;

  const quebrar = () => {
    document.querySelectorAll('.enner-home .parcelatorCollection > p:not(.enner-parcela-quebrada)').forEach((p) => {
      Array.from(p.childNodes).forEach((no) => {
        if (no.nodeType !== Node.TEXT_NODE) return;
        const partes = no.nodeValue.match(/^([\s\S]*?)\s*(sem juros)\s*$/i);
        if (!partes) return;
        no.nodeValue = partes[1];
        const resto = document.createElement('span');
        resto.className = 'enner-sem-juros';
        resto.textContent = partes[2];
        no.after(resto);
        p.classList.add('enner-parcela-quebrada');
      });
    });
  };

  let quadro = 0;
  const agendar = () => {
    cancelAnimationFrame(quadro);
    quadro = requestAnimationFrame(quebrar);
  };

  const iniciar = () => {
    quebrar();
    new MutationObserver(agendar).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', iniciar);
  else iniciar();
})();
